import React, { useState } from "react";
import { Loader2, CheckCircle2, Play, RotateCcw } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Card, CardHeader, CardTitle, CardContent } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { AddPortalUserForm } from "./add-portal-user-form";
import type { ApiClient } from "@cms/portal-lib/api";
import type { AdminClient } from "../lib/outlet-context";

interface ProvisionStep {
  key: string;
  label: string;
  description: string;
  done: (client: AdminClient) => boolean;
  rerunnable?: boolean;
  portalUser?: boolean;
  disabled?: (client: AdminClient) => boolean;
}

const STEPS: ProvisionStep[] = [
  {
    key: "db",
    label: "Tenant database",
    description: "Creates the D1 database and runs schema migrations.",
    done: (c: AdminClient) => !!c.d1_database_id,
  },
  {
    key: "r2",
    label: "Asset storage",
    description: "Creates the R2 bucket for media and static assets.",
    done: (c: AdminClient) => !!c.assets_bucket_name,
  },
  {
    key: "pages",
    label: "Pages project",
    description: "Creates the Cloudflare Pages project for site deployments.",
    done: (c: AdminClient) => !!c.pages_project_name,
  },
  {
    key: "build",
    label: "Trigger build",
    description: "Dispatches the GitHub Actions workflow to deploy the site.",
    done: () => false,
    rerunnable: true,
    disabled: (c: AdminClient) => !c.pages_project_name,
  },
  {
    key: "portal_user",
    label: "Portal user",
    description: "Invite the client's first portal user so they can sign in.",
    done: (c: AdminClient) => (c.user_count ?? 0) > 0,
    portalUser: true,
  },
];

export function ProvisionStepsPanel({ client, api, onClientUpdated }: {
  client: AdminClient;
  api: ApiClient | null;
  onClientUpdated: (client: AdminClient) => void;
}) {
  const [running, setRunning] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  const runStep = async (stepKey: string) => {
    if (!api) return;
    setRunning(stepKey);
    setErrors((prev) => ({ ...prev, [stepKey]: null }));

    try {
      await api.request(`/admin/clients/${client.id}/steps/${stepKey}`, { method: "POST" });
      const updated = await api.request<{ client: AdminClient }>(`/admin/clients/${client.id}`);
      onClientUpdated(updated.client);
    } catch (err) {
      setErrors((prev) => ({ ...prev, [stepKey]: (err as Error).message }));
    } finally {
      setRunning(null);
    }
  };

  // After adding a portal user inline, refetch the client so user_count (and
  // therefore this step's done state) updates.
  const refreshClient = async () => {
    if (!api) return;
    try {
      const updated = await api.request<{ client: AdminClient }>(`/admin/clients/${client.id}`);
      onClientUpdated(updated.client);
    } catch {
      // Non-fatal — the user was still created; the badge just won't flip yet.
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Provision steps</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {STEPS.map((step) => {
            const isDone = step.done(client);
            const isDisabled = step.disabled?.(client) ?? false;
            const isRunning = running === step.key;
            const error = errors[step.key];
            const canRun = !isDone || step.rerunnable;
            const runDisabled = isRunning || isDisabled || !api;
            const onRun = () => runStep(step.key);

            // Portal user step hosts the inline "add user" form rather than a
            // run button; once a user exists it shows Done like the others.
            if (step.portalUser) {
              return (
                <div key={step.key} className="rounded-lg border p-3.5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                          isDone ? "bg-success" : "bg-muted-foreground/30"
                        }`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{step.label}</p>
                        <p className="truncate text-xs text-muted-foreground">{step.description}</p>
                      </div>
                    </div>
                    {isDone && (
                      <Badge variant="success" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Done
                      </Badge>
                    )}
                  </div>
                  {!isDone && api && (
                    <AddPortalUserForm
                      client={client}
                      api={api}
                      onAdded={refreshClient}
                      className="mt-3"
                    />
                  )}
                </div>
              );
            }

            return (
              <div
                key={step.key}
                className="flex items-center justify-between gap-4 rounded-lg border p-3.5"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className={`shrink-0 h-2.5 w-2.5 rounded-full ${
                      isDone ? "bg-success" : "bg-muted-foreground/30"
                    }`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{step.label}</p>
                    <p className={`text-xs truncate ${error ? "text-destructive" : "text-muted-foreground"}`}>
                      {error ?? step.description}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {isDone && !step.rerunnable && (
                    <Badge variant="success" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Done
                    </Badge>
                  )}
                  {canRun && (
                    <Button
                      size="sm"
                      variant={isDone ? "outline" : "default"}
                      disabled={runDisabled}
                      onClick={onRun}
                    >
                      {isRunning ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : isDone ? (
                        <RotateCcw className="h-3.5 w-3.5" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      {isRunning ? "Running…" : isDone ? "Re-run" : "Run"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!api && (
          <p className="mt-3 text-xs text-muted-foreground">
            No API configured — steps cannot be executed.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
